"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useAuditTable = useAuditTable;
const DataTable_1 = require("@juvare/common-data-table-ui/DataTable");
const FiltersBar_1 = require("@juvare/common-data-table-ui/FiltersBar");
const utils_1 = require("@juvare/common-web-ui/utils");
const usePlatformSdkReactTranslation_1 = require("../../../../ui/hooks/usePlatformSdkReactTranslation");
const react_1 = __importDefault(require("react"));
const useAuditBackendDataProvider_1 = require("./useAuditBackendDataProvider");
function useAuditTable(auditTableParams) {
    const { t } = (0, usePlatformSdkReactTranslation_1.usePlatformSdkReactTranslation)();
    const filterEventTypes = react_1.default.useMemo(() => {
        const { eventTypes, additionalEventTypes } = auditTableParams;
        const predefinedEventTypes = [
            { value: 'create', label: t('audit.eventTypes.create'), group: 'Write' },
            { value: 'update', label: t('audit.eventTypes.update'), group: 'Write' },
            { value: 'delete', label: t('audit.eventTypes.delete'), group: 'Write' },
            { value: 'mass-create', label: t('audit.eventTypes.massCreate'), group: 'Write' },
            { value: 'mass-update', label: t('audit.eventTypes.massUpdate'), group: 'Write' },
            { value: 'mass-delete', label: t('audit.eventTypes.massDelete'), group: 'Write' },
            { value: 'list', label: t('audit.eventTypes.list'), group: 'Read' },
            { value: 'get', label: t('audit.eventTypes.get'), group: 'Read' },
            { value: 'login', label: t('audit.eventTypes.login'), group: 'Session' },
            { value: 'logout', label: t('audit.eventTypes.logout'), group: 'Session' },
            { value: 'switch-user', label: t('audit.eventTypes.switchUser'), group: 'Session' },
            { value: 'switch-back', label: t('audit.eventTypes.switchBack'), group: 'Session' },
        ];
        if (!eventTypes) {
            return [...predefinedEventTypes, ...(additionalEventTypes ?? [])];
        }
        else {
            return [
                ...predefinedEventTypes.filter(({ value: predefinedType }) => eventTypes.includes(predefinedType)),
                ...(additionalEventTypes ?? []),
            ];
        }
    }, [t, auditTableParams.eventTypes, auditTableParams.additionalEventTypes]);
    const filterAuditTags = react_1.default.useMemo(() => {
        return auditTableParams.auditTags || [];
    }, [t, auditTableParams.auditTags]);
    const filterEntityNames = react_1.default.useMemo(() => {
        return auditTableParams.entityFilter || [];
    }, [t, auditTableParams.entityFilter]);
    const filterConfig = (0, FiltersBar_1.useFilterConfig)((builder) => {
        builder.quickDateRangeFilter({
            dataPath: 'dateRange',
            label: t('audit.dateRange'),
            dataTest: 'timestamp-date-range-input',
        });
        builder.quickSearchFilter({
            dataPath: 'username',
            label: t('audit.userName'),
            dataTest: 'username-text-input',
        });
        builder.quickFilterMultiSelect({
            dataPath: 'eventType',
            label: t('audit.eventType'),
            values: filterEventTypes,
            widget: 'form.multiSelect',
            sx: { maxWidth: { xs: undefined, sm: 280 } },
            dataTest: 'event-type-input',
        });
        if (filterAuditTags.length > 0) {
            builder.quickFilterMultiSelect({
                dataPath: 'auditTags',
                label: t('audit.auditTags'),
                values: filterAuditTags,
                widget: 'form.multiSelect',
                sx: { maxWidth: { xs: undefined, sm: 280 } },
                dataTest: 'audit-tags-input',
            });
        }
        if (filterEntityNames.length > 0) {
            builder.quickFilterMultiSelect({
                dataPath: 'entities',
                label: t('audit.entity'),
                values: filterEntityNames,
                widget: 'form.multiSelect',
                sx: { maxWidth: { xs: undefined, sm: 280 } },
                dataTest: 'entity-input',
            });
        }
    }, [t]);
    const columns = react_1.default.useMemo(() => [
        {
            headerLabel: t('audit.dateTime'),
            cellFunction: (row) => react_1.default.createElement(DataTable_1.TextCell, null, (0, utils_1.formatDateTime)(new Date(row.timestamp))),
            sortKey: 'timestamp',
        },
        {
            headerLabel: t('audit.userName'),
            cellFunction: (row) => (react_1.default.createElement(DataTable_1.TextCell, null,
                row.originUserName && row.originUserName + ' as ',
                row.userName)),
        },
        {
            headerLabel: t('audit.eventType'),
            cellFunction: (row) => react_1.default.createElement(DataTable_1.TextCell, null, row.eventType),
        },
        {
            headerLabel: t('audit.entity'),
            cellFunction: (row) => react_1.default.createElement(DataTable_1.TextCell, null, row.entityName),
        },
        {
            headerLabel: t('audit.description'),
            cellFunction: (row) => react_1.default.createElement(DataTable_1.TextCell, null, row.description),
        },
    ], [t]);
    const params = {
        useDataProvider: (request) => {
            return auditTableParams.customAuditBackendDataProvider
                ? auditTableParams.customAuditBackendDataProvider(request, auditTableParams)
                : (0, useAuditBackendDataProvider_1.useAuditBackendDataProvider)(request, auditTableParams);
        },
        defaultSortColumnKey: 'timestamp',
        defaultSortColumnDesc: true,
        onRowClick: () => undefined,
        initialFilterData: auditTableParams?.initialFilterData ? auditTableParams.initialFilterData : undefined,
    };
    const { dataTableProps, filtersBarProps, appliedFilterData, clearActiveRow, reload, setSelectedRowIds, setActiveRowId } = auditTableParams.useDataTableFunction(params);
    return {
        dataTableProps: {
            ...dataTableProps,
            title: t('audit.tableTitle'),
            columns: columns,
            filtersBar: react_1.default.createElement(FiltersBar_1.FiltersBar, { ...filtersBarProps, config: filterConfig }),
            tableSx: { tableLayout: 'fixed' },
        },
        filtersBarProps: filtersBarProps,
        appliedFilterData: appliedFilterData,
        clearActiveRow: clearActiveRow,
        setSelectedRowIds: setSelectedRowIds,
        setActiveRowId: setActiveRowId,
        reload: reload,
    };
}
